import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeListOp } from '@/lib/shapes/types/shape-list-op'

/**
 * Inserts `shape` into `list` at `insertionIndex` (clamped into range), or appends when the
 * index is missing/invalid. No-ops if a shape with the same id already exists in `list`.
 */
export function withInsertedShapeAtIndex(
  list: ShapeData[],
  shape: ShapeData,
  insertionIndex?: number
): ShapeData[] {
  if (list.some((item) => item.id === shape.id)) return list
  if (typeof insertionIndex !== 'number' || !Number.isFinite(insertionIndex)) {
    return [...list, shape]
  }

  const clampedIndex = Math.max(0, Math.min(list.length, Math.floor(insertionIndex)))

  return [...list.slice(0, clampedIndex), shape, ...list.slice(clampedIndex)]
}

/**
 * Applies a sequence of insert/remove ops to `list` in a single synchronous pass, producing one
 * final array. Used to replay a compound add/remove undo/redo transaction (e.g. a group delete)
 * as one state commit instead of N sequential React state publishes, where only the last would
 * win under React 18 batching (WMPCM-5019/7165).
 */
export function applyShapeListOps(list: ShapeData[], ops: ShapeListOp[]): ShapeData[] {
  let next = list

  for (const op of ops) {
    next =
      op.kind === 'insert'
        ? withInsertedShapeAtIndex(next, op.shape, op.insertionIndex)
        : next.filter((shape) => shape.id !== op.shapeId)
  }

  return next
}
